'use client';

import { useState, useContext, useCallback, type FC, useRef } from 'react';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import type { Annotation } from '@/_types/documents';
import type { AnnotationLayerProps, DragState } from './AnnotationLayer.types';
import styles from './AnnotationLayer.module.css';
import { BASE_FONT_SIZE } from '../../PDFViewer';

export const AnnotationLayer: FC<AnnotationLayerProps> = ({
  annotations = [],
  selectedIds,
  selectionBox,
  onSelect,
  onUpdateAnnotation,
}) => {
  const { dimensions, mode } = useContext(PreviewContext);
  const dragRef = useRef<DragState | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const { width, height, scale, zoom } = dimensions;

  const handleDragStart = (e: React.MouseEvent, annotation: Annotation) => {
    if (mode !== 'move') return;

    if (selectedIds.size > 1 || !selectedIds.has(annotation.id)) {
      onSelect([annotation.id]);
    }

    e.stopPropagation();

    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);

    dragRef.current = {
      annotationId: annotation.id,
      startX: e.clientX,
      startY: e.clientY,
      originalLeft: annotation.position_data.left,
      originalBottom: annotation.position_data.bottom,
    };
  };

  const handleGlobalMouseMove = useCallback((e: MouseEvent) => {
    if (!dragRef.current) return;
    setDragOffset({
      x: e.clientX - dragRef.current.startX,
      y: e.clientY - dragRef.current.startY,
    });
  }, []);

  const handleGlobalMouseUp = useCallback(
    (e: MouseEvent) => {
      if (!dragRef.current) return;

      const annotation = annotations.find(
        (a) => a.id === dragRef.current?.annotationId,
      );
      if (!annotation) return;

      const pdfDeltaX = (e.clientX - dragRef.current.startX) / (scale * zoom);
      const pdfDeltaY = (e.clientY - dragRef.current.startY) / (scale * zoom);
      const maxLeft = width / scale - annotation.position_data.width;
      const maxBottom = height / scale - annotation.position_data.height;

      onUpdateAnnotation({
        ...annotation,
        position_data: {
          ...annotation.position_data,
          left: Math.round(
            Math.min(
              maxLeft,
              Math.max(0, dragRef.current.originalLeft + pdfDeltaX),
            ),
          ),
          bottom: Math.round(
            Math.min(
              maxBottom,
              Math.max(
                -height / scale,
                dragRef.current.originalBottom + pdfDeltaY,
              ),
            ),
          ),
        },
      });

      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      dragRef.current = null;
      setDragOffset({ x: 0, y: 0 });
    },
    [
      annotations,
      height,
      width,
      scale,
      zoom,
      onUpdateAnnotation,
      handleGlobalMouseMove,
    ],
  );

  return (
    <div className={styles.annotationLayer}>
      {annotations.map((annotation) => {
        const isDragging = dragRef.current?.annotationId === annotation.id;
        const isSelected = selectedIds.has(annotation.id);
        const color = annotation.style.split(': ')[1];
        const x = annotation.position_data.left * scale;
        const y =
          height -
          Math.abs(annotation.position_data.bottom) * scale -
          annotation.position_data.height * scale;

        return (
          <div
            key={annotation.id}
            data-id={annotation.id}
            className={styles.annotationItem}
            style={{
              left: `${x + (isDragging ? dragOffset.x : 0)}px`,
              top: `${y + (isDragging ? dragOffset.y : 0)}px`,
              height: `${annotation.position_data.height * scale}px`,
              width: `${annotation.position_data.width * scale}px`,
              position: 'absolute',
              backgroundColor: `${color}10`,
              border: `2px solid ${color}`,
              boxShadow: isSelected
                ? `0 0 0 2px white, 0 0 0 4px ${color}`
                : undefined,
              cursor:
                mode === 'move'
                  ? isDragging
                    ? 'grabbing'
                    : isSelected
                      ? 'grab'
                      : 'pointer'
                  : 'pointer',
              zIndex: isDragging ? 1000 : isSelected ? 2 : 1,
              transition: 'box-shadow 0.2s ease',
            }}
            onMouseDown={(e) => handleDragStart(e, annotation)}
          >
            {annotation.shape_type === 'check' && (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                style={{
                  width: '100%',
                  height: '100%',
                  padding: '10%',
                }}
              >
                <path
                  d="M4 12.6l5.3 5.3L20 7.4"
                  stroke={color}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {annotation.shape_type === 'textbox' && annotation.text && (
              <div
                style={{
                  color,
                  fontSize: `${BASE_FONT_SIZE}px`,
                  padding: '6px 12px',
                  height: '100%',
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  transform: `scale(${scale})`,
                  transformOrigin: 'center center',
                  boxSizing: 'border-box',
                  lineHeight: '1.2',
                }}
              >
                {annotation.text}
              </div>
            )}
          </div>
        );
      })}
      {selectionBox && (
        <div
          className={styles.annotationLayer__selectionBox}
          style={{
            left: `${Math.min(selectionBox.startX, selectionBox.endX)}px`,
            top: `${Math.min(selectionBox.startY, selectionBox.endY)}px`,
            width: `${Math.abs(selectionBox.endX - selectionBox.startX)}px`,
            height: `${Math.abs(selectionBox.endY - selectionBox.startY)}px`,
          }}
        />
      )}
    </div>
  );
};
